// src/useSpeech.js
// Hook pour synthèse vocale optimisé mobile

import { useEffect, useRef, useState, useCallback } from 'react';

export function useSpeech() {
  const synthRef = useRef(null);
  const [isSupported, setIsSupported] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [voices, setVoices] = useState([]);
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  useEffect(() => {
    // Vérifier le support de la synthèse vocale
    if ('speechSynthesis' in window) {
      synthRef.current = window.speechSynthesis;
      setIsSupported(true);
      
      console.log('✅ Synthèse vocale supportée');
      
      // Charger les voix disponibles
      const loadVoices = () => {
        const availableVoices = synthRef.current.getVoices();
        console.log('🎤 Voix disponibles:', availableVoices.length);
        
        // Filtrer les voix françaises
        const frenchVoices = availableVoices.filter(v => 
          v.lang.startsWith('fr') || v.lang === 'fr-FR'
        );
        console.log('🇫🇷 Voix françaises:', frenchVoices.map(v => v.name));
        
        setVoices(availableVoices);
        setIsReady(availableVoices.length > 0);
      };
      
      // iOS nécessite un événement pour charger les voix
      if (synthRef.current.onvoiceschanged !== undefined) {
        synthRef.current.onvoiceschanged = loadVoices;
      }
      
      // Charger immédiatement (fonctionne sur Android)
      loadVoices();
      
      // Retry après 100ms si pas de voix (fix pour certains navigateurs)
      setTimeout(loadVoices, 100);
    } else {
      console.warn('⚠️ Synthèse vocale NON supportée sur ce navigateur');
    }
    
    return () => {
      if (synthRef.current) {
        synthRef.current.cancel();
      }
    };
  }, []);
  
  const speak = useCallback((text, options = {}) => {
    if (!synthRef.current || !isReady) {
      console.warn('⚠️ Synthèse vocale non prête');
      return false;
    }
    
    if (!text || text.trim() === '') {
      console.warn('⚠️ Texte vide, pas de lecture');
      return false;
    }
    
    console.log('🔊 Lecture vocale:', text.substring(0, 50) + '...');
    
    // Annuler la parole en cours
    synthRef.current.cancel();
    
    // Créer l'utterance
    const utterance = new SpeechSynthesisUtterance(text);
    
    // Configuration
    utterance.lang = options.lang || 'fr-FR';
    utterance.rate = options.rate || 1.0;     // Vitesse (0.1 à 10)
    utterance.pitch = options.pitch || 1.0;   // Tonalité (0 à 2)
    utterance.volume = options.volume || 1.0; // Volume (0 à 1)
    
    // Sélectionner la meilleure voix française
    const frenchVoices = voices.filter(v => 
      v.lang.startsWith('fr') || v.lang === 'fr-FR'
    );
    
    if (frenchVoices.length > 0) {
      // Préférer les voix locales (meilleure qualité)
      const localVoice = frenchVoices.find(v => v.localService === true);
      const selectedVoice = localVoice || frenchVoices[0];
      
      console.log('🎤 Voix sélectionnée:', selectedVoice.name, selectedVoice.lang);
      utterance.voice = selectedVoice;
    }
    
    // Gestion du début
    utterance.onstart = () => {
      console.log('▶️ Lecture démarrée');
      setIsSpeaking(true);
    };
    
    // Gestion de la fin
    utterance.onend = () => {
      console.log('⏹️ Lecture terminée');
      setIsSpeaking(false);
    };
    
    // Gestion des erreurs
    utterance.onerror = (event) => {
      console.error('❌ Erreur TTS:', event.error);
      setIsSpeaking(false);
      
      // Sur iOS, si interruption, réessayer
      if (event.error === 'interrupted' || event.error === 'network') {
        console.log('🔄 Nouvelle tentative...');
        setTimeout(() => {
          if (synthRef.current) {
            synthRef.current.speak(utterance);
          }
        }, 100);
      }
    };
    
    // Lancer la synthèse vocale
    try {
      synthRef.current.speak(utterance);
      return true;
    } catch (error) {
      console.error('❌ Erreur lors du lancement:', error);
      setIsSpeaking(false);
      return false;
    }
  }, [isReady, voices]);
  
  const stop = useCallback(() => {
    if (synthRef.current) {
      console.log('⏹️ Arrêt de la lecture');
      synthRef.current.cancel();
      setIsSpeaking(false);
    }
  }, []);
  
  const pause = useCallback(() => {
    if (synthRef.current && isSpeaking) {
      console.log('⏸️ Pause de la lecture');
      synthRef.current.pause();
    }
  }, [isSpeaking]);
  
  const resume = useCallback(() => {
    if (synthRef.current && synthRef.current.paused) {
      console.log('▶️ Reprise de la lecture');
      synthRef.current.resume();
    }
  }, []);
  
  return { 
    speak, 
    stop, 
    pause,
    resume,
    isSupported,
    isReady,
    isSpeaking,
    voices 
  };
}